import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { trackError } from 'utils/trackError';
import { type RecordingMetadata, useRecordingSession } from 'providers/RecordingSessionProvider';
import type { ResumedState } from 'hooks/useRecordingWebSocket';
import { type WebSocketErrorState } from 'hooks/useRecordingWebSocket';
import { readErrorCode, useRecordingReconnect } from 'hooks/useRecordingReconnect';
import { PAUSED_CONSULTATIONS_QUERY_KEY } from 'hooks/usePausedConsultations';

import { isBrowserNotSupportedError } from 'utils/recordingErrors';
import { isMicNotFoundError } from 'utils/audio/classifyMicError';
import { stopStream } from 'utils/audio/stopStream';
import type { AnamnesisRecord, VisitReasonContent } from 'types/anamnesis';
import { createAudioRecorder } from 'utils/createAudioRecorder';
import { useChunkQueue } from 'hooks/useChunkQueue';
import { type ManualNotes } from 'hooks/useManualNotes';
import { useAudioDevice } from 'providers/AudioDeviceProvider';
import { useRecordingFlag } from 'hooks/useRecordingFlag';

const NOTES_AUTOSAVE_DEBOUNCE_MS = 500;

export type { WebSocketErrorState };

export const RecordingPhase = {
  IDLE: 'idle',
  RECORDING: 'recording',
  PAUSED: 'paused',
} as const;
export type RecordingPhase = (typeof RecordingPhase)[keyof typeof RecordingPhase];

export interface UseRecordingOptions {
  interactionIdFromLocation: string | undefined;
  startPaused?: boolean;
  initialElapsedTime?: number;
  initialDeviceNotFound?: boolean;
  selectedLanguageCode: string | undefined;
  currentAnamnesis: AnamnesisRecord | null;
  medatixxPatientId?: string;
  selectedVisitReason: VisitReasonContent | undefined;
  notes: ManualNotes;
  autoStart?: boolean;
  autoEnd?: boolean;
  withoutRecording?: boolean;
  micPermissionDenied?: boolean;
  onSuccess: (data: unknown, elapsedTime: number) => void;
  onResumed?: (state: ResumedState) => void;
}

export function useRecording({
  interactionIdFromLocation,
  startPaused = false,
  initialElapsedTime = 0,
  initialDeviceNotFound = false,
  selectedLanguageCode,
  currentAnamnesis,
  medatixxPatientId,
  selectedVisitReason,
  notes,
  autoStart = false,
  autoEnd = false,
  withoutRecording = false,
  micPermissionDenied = false,
  onSuccess,
  onResumed,
}: UseRecordingOptions) {
  const [recordingPhase, setRecordingPhase] = useState<RecordingPhase>(RecordingPhase.IDLE);
  const [elapsedTime, setElapsedTime] = useState(initialElapsedTime);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  mediaStreamRef.current = mediaStream;
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [webSocketError, setWebSocketError] = useState<WebSocketErrorState | null>(null);
  const webSocketErrorRef = useRef<WebSocketErrorState | null>(null);
  const [deviceNotFound, setDeviceNotFound] = useState(initialDeviceNotFound);
  const [browserNotSupported, setBrowserNotSupported] = useState(false);
  const [interactionId, setInteractionId] = useState<string | undefined>(interactionIdFromLocation);
  const hasAutoStarted = useRef(false);
  const hasAutoEnded = useRef(false);
  const [isContinuingWithoutRecording, setIsContinuingWithoutRecording] = useState(false);
  const isContinuingWithoutRecordingRef = useRef(false);
  const withoutRecordingInteractionIdRef = useRef<string | null>(null);
  const [isWithoutRecordingStarted, setIsWithoutRecordingStarted] = useState(false);
  const notesRef = useRef(notes);
  notesRef.current = notes;
  const onResumedRef = useRef(onResumed);
  onResumedRef.current = onResumed;

  const chunkQueue = useChunkQueue();
  const reconnect = useRecordingReconnect(chunkQueue);

  const {
    connect: wsConnect,
    start: wsStart,
    pause: wsPause,
    sendChunk: wsSendChunk,
    sendNotes: wsSendNotes,
    end: wsEnd,
    isProcessing,
    upstreamReconnect,
    disconnect: wsDisconnect,
  } = useRecordingSession();

  const audioDevice = useAudioDevice();
  const selectedDeviceId = audioDevice.selectedDevice?.deviceId;
  const canRecord = audioDevice.canRecord;
  const markRecording = useRecordingFlag();
  const queryClient = useQueryClient();

  const startInteraction = useCallback(
    async (metadata: RecordingMetadata) => {
      const started = await wsStart(metadata);
      if (started.resumed) {
        void queryClient.invalidateQueries({ queryKey: PAUSED_CONSULTATIONS_QUERY_KEY });
      }
      return started;
    },
    [wsStart, queryClient]
  );

  const notesSyncTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const pausePromiseRef = useRef<Promise<void>>(Promise.resolve());
  const [isPausing, setIsPausing] = useState(false);
  const scheduleNotesSync = () => {
    if (notesSyncTimeoutRef.current) clearTimeout(notesSyncTimeoutRef.current);
    notesSyncTimeoutRef.current = setTimeout(() => {
      wsSendNotes(notesRef.current);
    }, NOTES_AUTOSAVE_DEBOUNCE_MS);
  };

  useEffect(() => {
    if (initialDeviceNotFound) setDeviceNotFound(true);
  }, [initialDeviceNotFound]);

  const buildStartMetadata = useCallback(
    (): RecordingMetadata => ({
      language: selectedLanguageCode,
      anamnesisId: currentAnamnesis?.summary?.id,
      visitReasonId: selectedVisitReason?.id,
      medatixxId: currentAnamnesis?.medatixxPatientId ?? medatixxPatientId,
      fullname: currentAnamnesis?.patientName,
      birthdate: currentAnamnesis?.birthdate,
      notes: notesRef.current,
    }),
    [selectedLanguageCode, currentAnamnesis, selectedVisitReason, medatixxPatientId]
  );

  const reportError = useCallback((error: unknown) => {
    const { code, correlationId } = readErrorCode(error);
    const errorState = { code: code ?? 'unknown', correlationId };
    webSocketErrorRef.current = errorState;
    setWebSocketError(errorState);
  }, []);

  const startRecording = useCallback(async () => {
    setDeviceNotFound(false);
    webSocketErrorRef.current = null;
    setWebSocketError(null);
    reconnect.endSession();
    if (!canRecord) return;
    let stream: MediaStream | undefined;
    try {
      const { stream: acquiredStream, recorder: mediaRecorder } =
        await createAudioRecorder(selectedDeviceId);
      stream = acquiredStream;
      mediaRecorderRef.current = mediaRecorder;
      setMediaStream(stream);
      markRecording(true);

      const failRecording = (errorState: WebSocketErrorState) => {
        if (webSocketErrorRef.current !== null) return;
        webSocketErrorRef.current = errorState;
        setWebSocketError(errorState);
        setRecordingPhase(RecordingPhase.IDLE);
        if (mediaRecorder.state !== 'inactive') mediaRecorder.stop();
        stream?.getTracks().forEach((track) => track.stop());
        setMediaStream(null);
        markRecording(false);
        wsDisconnect();
      };

      const startMetadata: RecordingMetadata = {
        interactionId: interactionIdFromLocation,
        ...buildStartMetadata(),
      };
      reconnect.beginSession({
        metadata: startMetadata,
        getNotes: () => notesRef.current,
        isFailed: () => webSocketErrorRef.current !== null,
        failRecording,
      });

      // Chunks that arrive before the WS handshake completes are queued here
      // and drained in order once `wsStart` resolves.
      chunkQueue.init(
        wsSendChunk,
        reconnect.handleSendError,
        () => webSocketErrorRef.current !== null
      );

      mediaRecorder.ondataavailable = (event) => {
        if (typeof event.data === 'undefined' || webSocketErrorRef.current !== null) return;
        if (event.data.size === 0) return;
        reconnect.captureHeaderChunk(event.data);
        chunkQueue.enqueue(event.data);
      };

      // Start capturing immediately so the user perceives no delay.
      mediaRecorder.start(250);
      setRecordingPhase(startPaused ? RecordingPhase.PAUSED : RecordingPhase.RECORDING);
      if (startPaused) {
        mediaRecorder.pause();
      }

      // Set up the backend WS in parallel with the initial audio capture.
      await wsConnect();
      const started = await startInteraction(startMetadata);
      const startedInteractionId = started.interactionId || interactionIdFromLocation;
      setInteractionId(startedInteractionId);
      reconnect.setSessionMetadata({
        ...startMetadata,
        interactionId: startedInteractionId,
      });
      if (started.resumed) {
        setElapsedTime(started.resumed.elapsedSeconds);
        onResumedRef.current?.(started.resumed);
      }

      await chunkQueue.setReady();
    } catch (error) {
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      stream?.getTracks().forEach((track) => track.stop());
      setMediaStream(null);
      markRecording(false);
      setRecordingPhase(RecordingPhase.IDLE);
      wsDisconnect();
      if (isMicNotFoundError(error)) {
        setDeviceNotFound(true);
        return;
      }
      if (isBrowserNotSupportedError(error)) {
        setBrowserNotSupported(true);
        return;
      }
      trackError(error, { context: 'startRecording', action: 'getUserMedia' });
      void audioDevice.reevaluate();
    }
  }, [
    interactionIdFromLocation,
    startPaused,
    wsConnect,
    wsDisconnect,
    startInteraction,
    wsSendChunk,
    reconnect,
    buildStartMetadata,
    chunkQueue,
    canRecord,
    selectedDeviceId,
    markRecording,
    audioDevice,
  ]);

  const openSessionWithoutRecording = useCallback(
    async (resumeInteractionId?: string) => {
      await wsConnect();
      const started = await startInteraction({
        ...buildStartMetadata(),
        withoutRecording: true,
        interactionId: resumeInteractionId,
      });
      withoutRecordingInteractionIdRef.current = started.interactionId;
      setIsWithoutRecordingStarted(true);
      setInteractionId(started.interactionId);
      if (started.resumed) {
        setElapsedTime(started.resumed.elapsedSeconds);
        onResumedRef.current?.(started.resumed);
      }
    },
    [wsConnect, startInteraction, buildStartMetadata]
  );

  const continueWithoutRecording = useCallback(async () => {
    if (isContinuingWithoutRecordingRef.current) return;
    isContinuingWithoutRecordingRef.current = true;
    setIsContinuingWithoutRecording(true);
    try {
      await openSessionWithoutRecording();
      const data = await wsEnd();
      wsDisconnect();
      onSuccess(data, 0);
    } catch (error) {
      wsDisconnect();
      throw error;
    } finally {
      isContinuingWithoutRecordingRef.current = false;
      setIsContinuingWithoutRecording(false);
    }
  }, [openSessionWithoutRecording, wsEnd, wsDisconnect, onSuccess]);

  const startWithoutRecording = useCallback(async () => {
    webSocketErrorRef.current = null;
    setWebSocketError(null);
    setRecordingPhase(RecordingPhase.RECORDING);
    try {
      await openSessionWithoutRecording(interactionIdFromLocation);
    } catch (error) {
      setRecordingPhase(RecordingPhase.IDLE);
      wsDisconnect();
      reportError(error);
    }
  }, [openSessionWithoutRecording, interactionIdFromLocation, wsDisconnect, reportError]);

  const endPausedInteraction = useCallback(
    async (interactionId: string) => {
      try {
        await wsConnect();
        const started = await startInteraction({ interactionId });
        const data = await wsEnd();
        onSuccess(data, started.resumed?.elapsedSeconds ?? 0);
      } catch (error) {
        wsDisconnect();
        reportError(error);
      }
    },
    [wsConnect, startInteraction, wsEnd, wsDisconnect, onSuccess, reportError]
  );

  const pauseRecording = useCallback((): Promise<void> => {
    // Already paused: hand back the pause still waiting for its ack, so callers wait for it too.
    if (recordingPhase !== RecordingPhase.RECORDING) return pausePromiseRef.current;
    mediaRecorderRef.current?.pause();
    setRecordingPhase(RecordingPhase.PAUSED);
    // The caller may unmount right after this (e.g. leaving the screen), which
    // would cancel a debounced scheduleNotesSync before it fires — flush now.
    if (notesSyncTimeoutRef.current) clearTimeout(notesSyncTimeoutRef.current);
    wsSendNotes(notesRef.current);
    setIsPausing(true);
    const waitForAck = async () => {
      await wsPause();
      void queryClient.invalidateQueries({ queryKey: PAUSED_CONSULTATIONS_QUERY_KEY });
    };
    const paused = waitForAck().finally(() => {
      if (pausePromiseRef.current === paused) setIsPausing(false);
    });
    pausePromiseRef.current = paused;
    return paused;
  }, [recordingPhase, wsSendNotes, wsPause, queryClient]);

  const resumeRecording = useCallback(() => {
    if (recordingPhase !== RecordingPhase.PAUSED) return;
    mediaRecorderRef.current?.resume();
    setRecordingPhase(RecordingPhase.RECORDING);
    if (withoutRecording) {
      const interactionId = withoutRecordingInteractionIdRef.current;
      if (interactionId) wsStart({ interactionId }).catch(() => undefined);
      return;
    }
    const interactionId = reconnect.getInteractionId();
    if (interactionId) startInteraction({ interactionId }).catch(() => undefined);
  }, [recordingPhase, withoutRecording, reconnect, startInteraction, wsStart]);

  const toggleRecording = () => {
    if (recordingPhase === RecordingPhase.PAUSED) {
      resumeRecording();
    } else if (recordingPhase === RecordingPhase.RECORDING) {
      void pauseRecording();
    } else {
      void startRecording();
    }
  };

  const stopWithoutRecording = () => {
    if (!withoutRecordingInteractionIdRef.current) return;
    if (notesSyncTimeoutRef.current) clearTimeout(notesSyncTimeoutRef.current);
    setRecordingPhase(RecordingPhase.IDLE);
    wsSendNotes(notesRef.current);
    void wsEnd()
      .then((data) => {
        wsDisconnect();
        onSuccess(data, elapsedTime);
      })
      .catch(reportError);
  };

  const stopRecording = () => {
    if (notesSyncTimeoutRef.current) clearTimeout(notesSyncTimeoutRef.current);
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      const handleStop = () => {
        setRecordingPhase(RecordingPhase.IDLE);
        const finish = () => {
          if (webSocketErrorRef.current !== null) {
            wsDisconnect();
            return;
          }
          wsSendNotes(notesRef.current);
          void wsEnd()
            .then((data) => {
              reconnect.endSession();
              onSuccess(data, elapsedTime);
            })
            .catch(reportError);
        };
        // Let an in-flight reconnect settle first; end() needs a live session.
        const pendingReconnect = reconnect.pending();
        if (pendingReconnect === null) {
          finish();
        } else {
          void pendingReconnect.then(finish);
        }
      };
      // The recorder ends on its own when the microphone is unplugged; stopping it again would throw
      // and leave the consultation without an ending.
      if (recorder.state === 'inactive') {
        handleStop();
      } else {
        recorder.onstop = handleStop;
        recorder.stop();
      }
    }
    if (mediaStream) {
      stopStream(mediaStream);
      setMediaStream(null);
    }
    markRecording(false);
  };

  const abandonRecording = useCallback(() => {
    if (notesSyncTimeoutRef.current) clearTimeout(notesSyncTimeoutRef.current);
    const recorder = mediaRecorderRef.current;
    if (recorder) {
      recorder.ondataavailable = null;
      if (recorder.state !== 'inactive') recorder.stop();
    }
    if (mediaStreamRef.current) stopStream(mediaStreamRef.current);
    setMediaStream(null);
    setRecordingPhase(RecordingPhase.IDLE);
    markRecording(false);
    reconnect.endSession();
    wsDisconnect();
  }, [markRecording, reconnect, wsDisconnect]);

  // Decided once the device is known; a device that appears later shows the button, it does not start.
  const deviceSettled = audioDevice.ready && audioDevice.state !== 'searching';
  useEffect(() => {
    if (!autoStart || !(withoutRecording || deviceSettled) || hasAutoStarted.current) return;
    if (withoutRecording) {
      const timer = setTimeout(() => {
        hasAutoStarted.current = true;
        void startWithoutRecording();
      }, 0);
      return () => clearTimeout(timer);
    }
    hasAutoStarted.current = true;
    if (!micPermissionDenied && canRecord) void startRecording();
  }, [
    autoStart,
    withoutRecording,
    deviceSettled,
    micPermissionDenied,
    canRecord,
    startRecording,
    startWithoutRecording,
  ]);

  useEffect(() => {
    if (!autoEnd || !interactionIdFromLocation || hasAutoEnded.current) return;
    hasAutoEnded.current = true;
    void endPausedInteraction(interactionIdFromLocation);
  }, [autoEnd, interactionIdFromLocation, endPausedInteraction]);

  useEffect(() => {
    return () => {
      if (notesSyncTimeoutRef.current) clearTimeout(notesSyncTimeoutRef.current);
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (mediaStreamRef.current) stopStream(mediaStreamRef.current);
      markRecording(false);
    };
  }, [markRecording]);

  useEffect(() => {
    if (recordingPhase === RecordingPhase.RECORDING) {
      timerRef.current = setInterval(() => {
        setElapsedTime((prevTime) => prevTime + 1);
      }, 1000);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [recordingPhase]);

  const reconnectEpisode = reconnect.episode ?? upstreamReconnect;

  return {
    recordingPhase,
    elapsedTime,
    interactionId,
    mediaStream,
    webSocketError,
    deviceNotFound,
    browserNotSupported,
    reconnectEpisode,
    isProcessing,
    startRecording,
    continueWithoutRecording,
    endPausedInteraction,
    abandonRecording,
    isContinuingWithoutRecording,
    toggleRecording,
    pauseRecording,
    isPausing,
    stopRecording,
    stopWithoutRecording,
    isWithoutRecordingStarted,
    scheduleNotesSync,
    dismissDeviceNotFound: () => setDeviceNotFound(false),
  };
}
